import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { RouterLink } from '@angular/router';
import { EMPTY, Subscription, interval, of } from 'rxjs';
import { catchError, startWith, switchMap } from 'rxjs/operators';
import { MaterialModule } from '../../../material.module';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { UtcDatePipe } from '../../../pipes/utc-date.pipe';
import { MessageDialogComponent, MessageDialogData } from '../../../shared/message-dialog/message-dialog.component';
import { PatUpdateDialogComponent, PatUpdateDialogResult } from '../../../shared/pat-update-dialog/pat-update-dialog.component';
import { JsonViewDialogComponent } from '../../../shared/json-view-dialog/json-view-dialog.component';
import {
  BulkCodeValidationPreview,
  BulkCodeValidationRun,
  BulkCodeValidationRunJob,
  BulkCodeValidationService
} from '../../../services/bulk-code-validation.service';

@Component({
  selector: 'app-bulk-code-validation',
  templateUrl: './bulk-code-validation.component.html',
  styleUrls: ['./bulk-code-validation.component.scss'],
  imports: [CommonModule, FormsModule, MaterialModule, SpinnerComponent, UtcDatePipe, RouterLink]
})
export class BulkCodeValidationComponent implements OnInit, OnDestroy {
  private static readonly ACTIVE_STATUSES = ['Pending', 'Running', 'Paused'];

  run: BulkCodeValidationRun | null = null;
  latestRun: BulkCodeValidationRun | null = null;
  history: BulkCodeValidationRun[] = [];
  selectedRunId: number | null = null;
  preview: BulkCodeValidationPreview | null = null;
  jobs: BulkCodeValidationRunJob[] = [];
  jobsPage = 1;
  jobsPageSize = 25;
  jobsTotalCount = 0;
  jobsOutcomeFilter: 'all' | 'succeeded' | 'failed' | 'conflicts' = 'all';

  revalidateAfterDays = 30;
  defaultBranch = 'main';
  branchPreset: 'main' | 'master' | 'develop' | 'custom' = 'main';
  excludedKeyReasons: string[] = [];
  availableKeyReasons: string[] = ['Decommission / Retire', 'COTS', 'SaaS', 'Not in use', 'Sensitive data'];
  isStarting = false;
  isLoadingPreview = false;
  isStatusLoading = true;
  error: string | null = null;

  readonly jobColumns = ['id', 'status', 'orionId', 'repositoryUrl', 'branch', 'score', 'completedAt', 'tokens', 'outcome'];

  private statusSubscription?: Subscription;
  private previewSubscription?: Subscription;

  constructor(
    private readonly bulkService: BulkCodeValidationService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadPreview();
    this.loadHistory();

    this.statusSubscription = interval(5000)
      .pipe(
        startWith(0),
        switchMap(() => this.isStatusLoading || this.isRunActive
          ? this.bulkService.getStatus().pipe(catchError(() => of(null)))
          : EMPTY)
      )
      .subscribe(run => {
        this.isStatusLoading = false;
        const previousStatus = this.latestRun?.status;
        this.latestRun = run;

        if (run && previousStatus && previousStatus !== run.status) {
          this.loadHistory();
        }

        if (!run || (this.selectedRunId !== null && this.selectedRunId !== run.id)) {
          return;
        }

        const previousProcessed = this.run?.processedCount ?? -1;
        this.selectedRunId = run.id;
        this.run = run;

        if (this.jobs.length === 0 || run.processedCount !== previousProcessed) {
          this.loadJobs(run.id);
        }
      });
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.previewSubscription?.unsubscribe();
  }

  get isRunActive(): boolean {
    return !!this.latestRun && BulkCodeValidationComponent.ACTIVE_STATUSES.includes(this.latestRun.status);
  }

  get progressPercent(): number {
    if (!this.run || this.run.totalRepositories <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.run.processedCount / this.run.totalRepositories) * 100));
  }

  onBranchPresetChange(): void {
    if (this.branchPreset !== 'custom') {
      this.defaultBranch = this.branchPreset;
    }
    this.loadPreview();
  }

  loadPreview(): void {
    this.previewSubscription?.unsubscribe();
    this.isLoadingPreview = true;
    this.error = null;
    this.previewSubscription = this.bulkService
      .getPreview(this.revalidateAfterDays, this.defaultBranch, this.excludedKeyReasons)
      .subscribe({
      next: preview => {
        this.preview = preview;
        if (preview.availableKeyReasons?.length) {
          this.availableKeyReasons = preview.availableKeyReasons;
        }
        this.isLoadingPreview = false;
      },
      error: () => {
        this.error = 'Failed to calculate the validation scope.';
        this.isLoadingPreview = false;
      }
    });
  }

  onStart(): void {
    if (this.isRunActive || this.isStarting) {
      return;
    }

    const patDialog = this.dialog.open(PatUpdateDialogComponent, {
      width: '700px',
      disableClose: true,
      data: {
        title: 'GitHub PAT for Bulk Code Validation',
        intro: 'Every repository in this run is read with your GitHub Enterprise Personal Access Token. '
          + 'Please verify or update it before continuing.',
        provider: 'github'
      }
    });

    patDialog.afterClosed().subscribe((patResult: PatUpdateDialogResult | undefined) => {
      if (!patResult?.proceed) {
        return;
      }
      this.confirmAndStart();
    });
  }

  private confirmAndStart(): void {
    this.previewSubscription?.unsubscribe();
    this.error = null;
    this.previewSubscription = this.bulkService
      .getPreview(this.revalidateAfterDays, this.defaultBranch, this.excludedKeyReasons)
      .subscribe({
      next: preview => {
        this.preview = preview;

        const exclusionSentence = preview.excludedKeyReasons?.length
          ? ` ${preview.skippedExcludedCount} repositor(ies) are excluded by the selected CMDB filter(s) (${preview.excludedKeyReasons.join(', ')}).`
          : '';

        const confirmDialog = this.dialog.open(MessageDialogComponent, {
          width: '520px',
          disableClose: true,
          data: {
            title: 'Start Bulk Code Validation?',
            message:
              `${preview.eligibleRepositoryCount} GitHub repositor(ies) across ${preview.eligibleCount} application(s) `
              + `will be validated on branch "${preview.defaultBranch}". `
              + `${preview.skippedRecentlyValidatedCount} will be skipped because they were validated in the last `
              + `${preview.revalidateAfterDays} day(s).${exclusionSentence} `
              + 'This runs against GitHub with your PAT and may take a while.',
            confirmMode: true,
            confirmLabel: 'Start Run',
            type: 'warning'
          } as MessageDialogData
        });

        confirmDialog.afterClosed().subscribe(confirmed => {
          if (confirmed) {
            this.startRun();
          }
        });
      },
      error: () => {
        this.error = 'Failed to calculate the validation scope.';
      }
    });
  }

  private startRun(): void {
    this.isStarting = true;
    this.error = null;

    this.bulkService.start(this.revalidateAfterDays, this.defaultBranch, this.excludedKeyReasons).subscribe({
      next: run => {
        this.run = run;
        this.latestRun = run;
        this.selectedRunId = run.id;
        this.jobs = [];
        this.jobsPage = 1;
        this.jobsTotalCount = 0;
        this.jobsOutcomeFilter = 'all';
        this.isStarting = false;
        this.loadHistory();
      },
      error: err => {
        this.isStarting = false;
        this.error = err.error?.message || 'Failed to start the bulk code validation run.';
      }
    });
  }

  onPause(): void {
    this.transition(runId => this.bulkService.pause(runId));
  }

  onResume(): void {
    this.transition(runId => this.bulkService.resume(runId));
  }

  onCancel(): void {
    if (!this.latestRun) {
      return;
    }

    const confirmDialog = this.dialog.open(MessageDialogComponent, {
      width: '460px',
      disableClose: true,
      data: {
        title: 'Cancel Run?',
        message: 'Applications that have not been validated yet will be left untouched. This cannot be undone.',
        confirmMode: true,
        confirmLabel: 'Cancel Run',
        type: 'warning'
      } as MessageDialogData
    });

    confirmDialog.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.transition(runId => this.bulkService.cancel(runId));
      }
    });
  }

  private transition(action: (runId: number) => ReturnType<BulkCodeValidationService['pause']>): void {
    if (!this.latestRun) {
      return;
    }

    action(this.latestRun.id).subscribe({
      next: run => {
        this.latestRun = run;
        if (this.selectedRunId === run.id) {
          this.run = run;
        }
        this.error = null;
      },
      error: err => {
        this.error = err.error?.message || 'Failed to update the run.';
      }
    });
  }

  onJobsPageChange(event: PageEvent): void {
    this.jobsPage = event.pageIndex + 1;
    this.jobsPageSize = event.pageSize;
    if (this.run) {
      this.loadJobs(this.run.id);
    }
  }

  onJobsOutcomeFilterChange(): void {
    this.jobsPage = 1;
    if (this.run) {
      this.loadJobs(this.run.id);
    }
  }

  onRunSelectionChange(runId: number): void {
    this.selectedRunId = Number(runId);
    this.run = this.selectedRunId === this.latestRun?.id
      ? this.latestRun
      : this.history.find(r => r.id === this.selectedRunId) ?? null;
    this.jobs = [];
    this.jobsPage = 1;
    this.jobsTotalCount = 0;
    this.jobsOutcomeFilter = 'all';

    if (this.run) {
      this.loadJobs(this.run.id);
    }
  }

  showSucceededJobs(): void {
    this.jobsOutcomeFilter = 'succeeded';
    this.onJobsOutcomeFilterChange();
  }

  showFailedJobs(): void {
    this.jobsOutcomeFilter = 'failed';
    this.onJobsOutcomeFilterChange();
  }

  showConflictJobs(): void {
    this.jobsOutcomeFilter = 'conflicts';
    this.onJobsOutcomeFilterChange();
  }

  private loadHistory(): void {
    this.bulkService.getHistory(1, 20).subscribe({
      next: result => {
        this.history = result.data;
      },
      error: () => {
        // Best-effort: the current run remains visible without the history list.
      }
    });
  }

  private loadJobs(runId: number): void {
    this.bulkService.getRunJobs(runId, this.jobsPage, this.jobsPageSize, this.jobsOutcomeFilter).subscribe({
      next: result => {
        this.jobs = result.data;
        this.jobsTotalCount = result.totalRecords;
      },
      error: () => {
        // Best-effort: the progress card remains usable without the job list.
      }
    });
  }

  getStatusClass(status: string | number): string {
    switch (String(status)) {
      case 'Completed':
      case '2':
        return 'badge-success';
      case 'Running':
      case '1':
        return 'badge-primary';
      case 'Failed':
      case '3':
        return 'badge-danger';
      case 'Paused':
      case 'Cancelled':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }

  getJobStatusLabel(status: string | number): string {
    switch (String(status)) {
      case '0': return 'Pending';
      case '1': return 'Running';
      case '2': return 'Completed';
      case '3': return 'Failed';
      default: return String(status);
    }
  }

  getRepoName(repositoryUrl: string): string {
    if (!repositoryUrl) {
      return '-';
    }
    const parts = repositoryUrl.split('/').filter(p => p.length > 0);
    return parts.length >= 2 ? `${parts[parts.length - 2]}/${parts[parts.length - 1]}` : repositoryUrl;
  }

  formatTokenCount(tokens: number): string {
    if (!tokens) {
      return '0';
    }
    if (tokens >= 1_000_000) {
      return `${(tokens / 1_000_000).toFixed(1)}M`;
    }
    if (tokens >= 1_000) {
      return `${(tokens / 1_000).toFixed(1)}K`;
    }
    return tokens.toString();
  }

  viewAiJson(job: BulkCodeValidationRunJob): void {
    if (!job.lightweightResult) {
      return;
    }

    this.dialog.open(JsonViewDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        title: `AI Validation Result — Job #${job.id} — ${this.getRepoName(job.repositoryUrl)}`,
        json: JSON.stringify(job.lightweightResult, null, 2)
      }
    });
  }

  isCmdbConflict(job: BulkCodeValidationRunJob): boolean {
    return job.lightweightResult?.cmdbComparison === 'ConflictDetected';
  }

  getOutcomeLabel(job: BulkCodeValidationRunJob): string {
    const status = this.getJobStatusLabel(job.status);
    if (status === 'Failed') {
      return 'Validation failed';
    }
    if (status !== 'Completed') {
      return 'Not completed';
    }

    switch (job.lightweightResult?.cmdbComparison) {
      case 'ConflictDetected': return 'CMDB conflict';
      case 'RecommendFullAssessment': return 'Full assessment recommended';
      case 'ValidateCmdb': return 'Review CMDB data';
      case 'LikelyModern': return 'Likely modern';
      case 'LegacyIndicatorsFound': return 'Legacy indicators found';
      case 'InsufficientSignals': return 'Insufficient signals';
      case 'UnableToValidate': return 'Unable to validate';
      default: return 'No comparison result';
    }
  }

  getRunOptionLabel(run: BulkCodeValidationRun): string {
    const started = run.startedAt ?? run.createdAt;
    const date = started ? new Date(started).toLocaleString() : 'not started';
    return `Run #${run.id} - ${run.status} - ${date}`;
  }
}
